import {
    Absence,
    AbsenceStatus,
    type DateRange,
    DeleteAbsenceRequestParams,
    TimeEntryType,
    dateAdd,
    dateSub,
    formatAbsenceDayCount,
    formatDate,
    formatDateShort,
    getDateArray,
    getRange,
    isHalfDayEntry,
    monthNames,
    parseDateString,
    timeEntryTypeColor,
    toDateString,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { styleMap } from "lit/directives/style-map.js";
import { app } from "../../init";
import { singleton } from "../../lib/singleton";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import { alert, confirm } from "../alert-dialog";
import "../scroller";
import "./request-absence-dialog";
import type { RequestAbsenceDialog } from "./request-absence-dialog";

@customElement("ptc-staff-absences")
export class Absences extends Routing(StateMixin(LitElement)) {
    routePattern = /^(home|absences)/;

    async connectedCallback() {
        super.connectedCallback();
        await app.loaded;
        if (app.account) {
            void this._load();
        }
    }

    @property({ reflect: true, type: Boolean })
    expanded = false;

    @state()
    private _loading = false;

    @state()
    private _absences: Absence[] = [];

    @state()
    private _displayPastAbsences = false;

    @singleton("ptc-staff-request-absence-dialog")
    private _requestAbsenceDialog: RequestAbsenceDialog;

    private get _employee() {
        return app.profile;
    }

    refresh() {
        return this._load();
    }

    private async _load() {
        if (!this._employee) {
            return;
        }

        this._loading = true;
        this._displayPastAbsences = false;

        try {
            this._absences = await app.api.getAbsences({ employee: [this._employee.id] });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _editAbsenceRequest(absence: Absence) {
        const edited = await this._requestAbsenceDialog.show(absence);
        if (edited) {
            void alert("Deine Änderung wurde übernommen und dein Arbeitgeber informiert.", {
                title: "Änderung Erfolgreich",
                type: "success",
            });
            void this._load();
        }
    }

    private async _deleteAbsenceRequest(absence: Absence) {
        if (
            !(await confirm(
                `Möchtest du ${absence.type === TimeEntryType.Vacation ? "diesen Urlaubsantrag" : "diese Krankmeldung"} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
                "Löschen",
                "Abbrechen",
                {
                    title: `${absence.type === TimeEntryType.Vacation ? "Urlaubsantrag" : "Krankmeldung"} Löschen`,
                    type: "destructive",
                }
            ))
        ) {
            return;
        }

        this._loading = true;
        try {
            await app.api.deleteAbsenceRequest(new DeleteAbsenceRequestParams({ id: absence.id }));
            void alert(
                `${absence.type === TimeEntryType.Vacation ? "Dein Urlaubsantrag" : "Deine Krankmeldung"} wurde erfolgreich gelöscht. Dein Arbeitgeber wurde informiert.`,
                {
                    title: `${absence.type === TimeEntryType.Vacation ? "Urlaubsantrag" : "Krankmeldung"} Gelöscht`,
                    type: "success",
                }
            );
            void this._load();
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._loading = false;
    }

    private async _newVacationRequest() {
        const created = await this._requestAbsenceDialog.show(new Absence({ type: TimeEntryType.Vacation }));
        if (created) {
            void alert(
                "Dein Urlaubsantrag wurde erfolgreich an deinen Arbeitgeber übermittelt. Du wirst informiert, sobald dieser bearbeitet wurde.",
                { title: "Urlaubsantrag Gesendet", type: "success" }
            );
            void this._load();
        }
    }

    private async _reportSick() {
        const created = await this._requestAbsenceDialog.show(new Absence({ type: TimeEntryType.Sick }));
        if (created) {
            void alert(
                "Deine Krankmeldung wurde erfolgreich an deinen Arbeitgeber übermittelt. Du wirst informiert, sobald diese bearbeitet wurde.",
                { title: "Krankmeldung Gesendet", type: "success" }
            );
            void this._load();
        }
    }

    static styles = [
        shared,
        css`
            :host {
                position: relative;
            }

            :host(:not([expanded])) .hide-when-collapsed {
                display: none;
            }

            .absence {
                border: solid 1px var(--shade-2);
                border-radius: 1em;
                padding-top: 0.5em;
                margin-top: 0;
            }

            .days-grid {
                display: grid;
                grid-template-columns: repeat(7, 1fr);
                grid-gap: 0.5em 0;
            }

            .day {
                text-align: center;
                height: 2em;
                box-sizing: border-box;
                position: relative;
            }

            .day.start,
            .day.middle,
            .day.end {
                border-top: solid 1px var(--color-highlight);
                border-bottom: solid 1px var(--color-highlight);
                background: var(--color-highlight);
                color: var(--color-bg);
            }

            .day.start {
                border-left: solid 1px var(--color-highlight);
                border-top-left-radius: 0.5em;
                border-bottom-left-radius: 0.5em;
            }

            .day.end {
                border-right: solid 1px var(--color-highlight);
                border-top-right-radius: 0.5em;
                border-bottom-right-radius: 0.5em;
            }

            .day.half-day {
                background: color-mix(in oklch, var(--color-highlight), transparent);

                &::before {
                    content: "";
                    display: block;
                    width: 100%;
                    height: 100%;
                    background: var(--color-highlight);
                    position: absolute;
                    inset: 0;
                    z-index: -1;
                    clip-path: var(--path-diagonal-half);
                    border-radius: inherit;
                }
            }

            .absence.denied .day,
            .absence.requested .day {
                color: var(--color-highlight);
                background: transparent;
            }

            .absence.denied .day:not(.before):not(.after)::after {
                content: "";
                display: block;
                width: 110%;
                height: 1px;
                background: currentColor;
                position: absolute;
                top: 0;
                bottom: 0;
                margin: auto;
                left: -5%;
            }

            .today {
                background: var(--color-highlight);
                color: var(--color-bg);
                padding: 0.1em;
                border-radius: 100%;
                width: 1.5em;
                height: 1.5em;
                line-height: 1.5em;
                font-weight: bold;
            }

            .approved .day {
                &.start,
                &.middle,
                &.end {
                    border: none;

                    .today {
                        background: var(--color-bg);
                        color: var(--color-highlight);
                    }
                }
            }
            .preview {
                --day-width: 0.5rem;
            }

            .preview-inner {
                padding: 0.5em 2em;
            }

            .preview-timeline-month {
                box-sizing: border-box;
                border-top: solid 2px var(--shade-3);
                text-align: center;
                position: relative;
            }

            .preview-timeline-month::before {
                content: "";
                display: block;
                height: 1.5em;
                position: absolute;
                left: 0;
                top: -1em;
                border-left: solid 2px var(--shade-3);
            }

            .preview-absences {
                position: relative;
                height: 4em;
            }

            .preview-absence {
                position: absolute;
                background: var(--color-highlight);
                left: calc((var(--absence-offset)) * var(--day-width));
                width: calc((var(--absence-duration)) * var(--day-width));
                border-radius: 0.5em;
                top: 1.8em;
                height: 0.8em;
                overflow: visible;
                z-index: 1;
                border: solid 2px transparent;
            }

            .preview-absence.requested {
                background: var(--color-bg);
                border-color: var(--color-highlight);
            }

            .preview-absence-label {
                position: absolute;
                top: 0;
                left: calc((var(--absence-offset)) * var(--day-width));
                transform: translateX(-50%);
                width: fit-content;

                &:nth-of-type(odd) {
                    top: 1em;
                }
            }

            .preview-today {
                position: absolute;
                top: -2em;
                height: 3em;
                border-left: dashed 2px var(--orange);
            }

            .preview-timeline-week {
                position: absolute;
                top: -0.5em;
                height: 0.8em;
                border-left: solid 1px var(--shade-3);
            }

            .overview {
                margin-bottom: calc(2 * var(--spacing));
            }
        `,
    ];
    private _fomatDateText(absence: Absence, numeric = false) {
        return dateSub(absence.start, absence.end) === 1
            ? `${formatDateShort(absence.start, numeric)}`
            : `${formatDateShort(absence.start, numeric)} -
                      ${formatDateShort(dateAdd(absence.end, { days: -1 }), numeric)}`;
    }

    private _renderOverview() {
        const today = toDateString(new Date());
        const from = getRange(today, "month").from;
        const to = dateAdd(from, { months: 12 });
        let first = getRange(from, "month").from;
        const months: DateRange[] = [];
        while (first < to) {
            months.push(getRange(first, "month"));
            first = dateAdd(first, { months: 1 });
        }
        const absences = this._absences.filter((a) => a.end > from && a.start < to);
        const mondays: DateString[] = [];
        let monday = getRange(from, "week").to;
        while (monday < to) {
            mondays.push(monday);
            monday = dateAdd(monday, { days: 7 });
        }

        return html`
            <div class="preview horizontal scroller hide-scrollbar">
                <div class="preview-inner" style="width: calc(${dateSub(from, to)} * var(--day-width))">
                    <div class="preview-absences relative">
                        <div>
                            ${absences.map(
                                (absence) => html`
                                    <div
                                        class="preview-absence ${absence.status} relative"
                                        style=${styleMap({
                                            "--color-highlight": timeEntryTypeColor(absence.type),
                                            "--absence-offset": dateSub(from, absence.start) + 1,
                                            "--absence-duration": dateSub(absence.start, absence.end),
                                        })}
                                    ></div>
                                `
                            )}
                        </div>
                        <div>
                            ${absences.map(
                                (absence) =>
                                    html`<div
                                        class="smaller bold preview-absence-label nowrap date-text"
                                        style=${styleMap({
                                            "--color-highlight": timeEntryTypeColor(absence.type),
                                            "--absence-offset":
                                                dateSub(from, absence.start) +
                                                1 +
                                                dateSub(absence.start, absence.end) / 2,
                                        })}
                                    >
                                        ${this._fomatDateText(absence, true)}
                                    </div> `
                            )}
                        </div>
                    </div>
                    <div class="preview-timeline horizontal layout bottom-margined relative">
                        ${months.map(({ from, to }, i) => {
                            const d = parseDateString(from)!;
                            return html`
                                <div
                                    class="preview-timeline-month semibold"
                                    style="width: calc(${dateSub(from, to)} * var(--day-width))"
                                >
                                    <span class="subtle">
                                        ${monthNames[d.getMonth()].slice(0, 3)}
                                        ${i === 0 || d.getMonth() === 0 ? d.getFullYear() : ""}
                                    </span>
                                </div>
                            `;
                        })}
                        ${mondays.map(
                            (date) => html`
                                <div
                                    class="preview-timeline-week"
                                    style="left: calc(${dateSub(from, date)} * var(--day-width))"
                                ></div>
                            `
                        )}
                        <div class="preview-today" style="left: calc(${dateSub(from, today)} * var(--day-width))"></div>
                    </div>
                </div>
            </div>
        `;
    }

    render() {
        const emp = this._employee;
        if (!emp) {
            return;
        }
        const today = toDateString(new Date());

        const absences = this._displayPastAbsences ? this._absences : this._absences.filter((a) => a.end > today);

        return html`
            <div class="fullbleed vertical layout">
                <div class="overview">${this._renderOverview()}</div>
                ${!absences.length
                    ? html`
                          <div class="stretch centering vertical layout">
                              <ptc-ui-icon name="icon.time-entry.vacation" class="giant faded"></ptc-ui-icon>
                              <div>Es liegen aktuell keine geplanten Abwesenheiten vor.</div>
                              <button
                                  class="subtle double-margined"
                                  @click=${() => (this._displayPastAbsences = true)}
                                  ?hidden=${this._displayPastAbsences}
                              >
                                  Vergangene Abwesenheiten Anzeigen
                              </button>
                          </div>
                      `
                    : html`
                          <ptc-scroller class="stretch hide-when-collapsed">
                              ${absences
                                  .sort((a, b) => b.start.localeCompare(a.start))
                                  .map((absence) => {
                                      const { start, end, type, status, dayCount, request, response } = absence;
                                      const inclusiveEnd = dateAdd(end, { days: -1 });
                                      const renderFrom = getRange(start, "week").from;
                                      const renderTo = getRange(inclusiveEnd, "week").to;
                                      const dates = getDateArray(renderFrom, renderTo);

                                      return html`
                                          <div
                                              class="double-padded double-margined absence ${status}"
                                              style="--color-highlight: ${timeEntryTypeColor(type)}"
                                          >
                                              <div class="medium vertically-padded horizontal layout">
                                                  <div class="bold stretch">
                                                      <span class="colored-text">
                                                          <ptc-ui-icon
                                                              name="${app.localized.timeEntryTypeIcon(type)}"
                                                          ></ptc-ui-icon>
                                                          ${app.localized.timeEntryTypeLabel({
                                                              type: type,
                                                          })}:
                                                      </span>
                                                      <strong> ${this._fomatDateText(absence)}</strong>
                                                  </div>
                                                  <div class="smaller">
                                                      ${status === AbsenceStatus.Requested
                                                          ? html`
                                                                <div class="orange pill">
                                                                    ${absence.type.includes("sick")
                                                                        ? "Eingereicht"
                                                                        : "Beantragt"}
                                                                </div>
                                                            `
                                                          : status === AbsenceStatus.Denied
                                                            ? html` <div class="red pill">Abgelehnt</div> `
                                                            : html`
                                                                  <div class="pill">
                                                                      <strong>
                                                                          ${formatAbsenceDayCount(dayCount)}
                                                                      </strong>
                                                                  </div>
                                                              `}
                                                  </div>
                                              </div>

                                              ${request
                                                  ? html`
                                                        <div
                                                            class="padded vertically-margined text-centering orange box"
                                                        >
                                                            <strong>
                                                                <ptc-ui-icon
                                                                    name="icon.planning.vacation-request"
                                                                ></ptc-ui-icon>
                                                                ${type.includes("sick") ? "Eingereicht" : "Beantragt"}
                                                                am ${formatDate(request.time)}
                                                            </strong>
                                                            ${request.message
                                                                ? html` - <span>"${request.message}"</span> `
                                                                : ""}
                                                        </div>
                                                    `
                                                  : ""}
                                              ${response
                                                  ? html`
                                                        ${status === AbsenceStatus.Denied
                                                            ? html`
                                                                  <div
                                                                      class="padded vertically-margined text-centering red box"
                                                                  >
                                                                      <strong>
                                                                          <ptc-ui-icon
                                                                              name="icon.status.rejected"
                                                                          ></ptc-ui-icon>
                                                                          Abgelehnt von
                                                                          ${response.employeeName || "Unbekannt"} am
                                                                          ${formatDate(response.time)}
                                                                      </strong>
                                                                      ${response.message
                                                                          ? html`
                                                                                -
                                                                                <span>"${response.message}"</span>
                                                                            `
                                                                          : ""}
                                                                  </div>
                                                              `
                                                            : html`
                                                                  <div
                                                                      class="padded vertically-margined text-centering green box"
                                                                  >
                                                                      <strong>
                                                                          <ptc-ui-icon
                                                                              name="icon.status.approved"
                                                                          ></ptc-ui-icon>
                                                                          Genehmigt von
                                                                          ${response.employeeName || "Unbekannt"} am
                                                                          ${formatDate(response.time)}
                                                                      </strong>
                                                                      ${response.message
                                                                          ? html`
                                                                                -
                                                                                <span>"${response.message}"</span>
                                                                            `
                                                                          : ""}
                                                                  </div>
                                                              `}
                                                    `
                                                  : ""}

                                              <div class="days-grid vertically-padded">
                                                  <div class="text-centering bold">Mo</div>
                                                  <div class="text-centering bold">Di</div>
                                                  <div class="text-centering bold">Mi</div>
                                                  <div class="text-centering bold">Do</div>
                                                  <div class="text-centering bold">Fr</div>
                                                  <div class="text-centering bold">Sa</div>
                                                  <div class="text-centering bold">So</div>

                                                  ${dates.map((date) => {
                                                      const entry = absence.entries?.find((e) => e.date === date);
                                                      const isHalfDay = isHalfDayEntry(entry);

                                                      return html`
                                                          <div
                                                              class="day centering layout ${classMap({
                                                                  before: date < start,
                                                                  start: date === start,
                                                                  middle: date > start && date < inclusiveEnd,
                                                                  end: date === inclusiveEnd,
                                                                  after: date > inclusiveEnd,
                                                                  "half-day": isHalfDay,
                                                              })}"
                                                          >
                                                              <div class="${date === today ? "today" : ""}">
                                                                  ${parseDateString(date)!.getDate()}
                                                              </div>
                                                          </div>
                                                      `;
                                                  })}
                                              </div>

                                              ${absence.status === AbsenceStatus.Requested
                                                  ? html`
                                                        <div
                                                            class="top-margined horizontal spacing end-justifying horizontal layout"
                                                        >
                                                            <button
                                                                class="slim black ghost"
                                                                @click=${() => this._editAbsenceRequest(absence)}
                                                            >
                                                                <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon>
                                                                Bearbeiten
                                                            </button>
                                                            <button
                                                                class="slim red ghost"
                                                                @click=${() => this._deleteAbsenceRequest(absence)}
                                                            >
                                                                <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                                                                Löschen
                                                            </button>
                                                        </div>
                                                    `
                                                  : ""}
                                          </div>
                                      `;
                                  })}
                              <div
                                  class="centering small double-margined layout hide-when-collapsed"
                                  ?hidden=${this._displayPastAbsences}
                              >
                                  <button class="subtle" @click=${() => (this._displayPastAbsences = true)}>
                                      Vergangene Abwesenheiten Anzeigen
                                  </button>
                              </div>
                          </ptc-scroller>
                      `}

                <div class="double-margined spacing evenly stretching horizontal layout hide-when-collapsed">
                    <button
                        class="ghost"
                        @click=${this._newVacationRequest}
                        style="--color-highlight: ${timeEntryTypeColor(TimeEntryType.Vacation)}"
                        ?hidden=${!app.hasPermission("staff.absences.request_vacation")}
                    >
                        <ptc-ui-icon name="${app.localized.timeEntryTypeIcon(TimeEntryType.Vacation)}"></ptc-ui-icon>
                        Urlaub Beantragen
                    </button>

                    <button
                        class="ghost"
                        style="--color-highlight: ${timeEntryTypeColor(TimeEntryType.Sick)}"
                        @click=${this._reportSick}
                        ?hidden=${!app.hasPermission("staff.absences.request_sick")}
                    >
                        <ptc-ui-icon name="${app.localized.timeEntryTypeIcon(TimeEntryType.Sick)}"></ptc-ui-icon> Krank
                        Melden
                    </button>
                </div>
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
